/**
 * 외관(색) 해석 — 순수 함수(three.js 없이 시험한다). SR-MDF-06 · ARC §6 MDF appearance.
 *
 *  - MDF v1 `appearance{palette, colors}`: colors 는 부품 인스턴스 id(예: "6275844_101") → 팔레트 키.
 *  - Campus 팔레트(IF-10 `GET /v1/public/3d/palette` · 셸은 `GET /api/palette`): `{palette, colors:[{key, srgb}]}`.
 *  - 팔레트를 못 받았거나 버전이 다르면 모형 원래 색(오류 아님). 알 수 없는 인스턴스 id·팔레트 키는 무시한다.
 */

/** MDF v1 appearance 와 같은 모양(@picnic-field/mdf MdfAppearance) — 렌더러가 mdf 패키지에 기대지 않게 구조로만 받는다 */
export interface Appearance {
  palette: string;
  colors: Record<string, string>;
}

export interface PaletteColor {
  key: string;
  /** "#RRGGBB" sRGB */
  srgb: string;
  name?: string;
}

export interface Palette {
  palette: string;
  colors: readonly PaletteColor[];
}

/**
 * 이름 → 팔레트(2026-09-30 팔레트 버전 전환 — Campus 현행 brick-v1 → smartblock-v1). 작품마다 제 appearance.palette 이름으로 고른다
 * (옛 brick-v1 작품은 brick-v1 색). 여러 작품을 한 장면에 그리는 화면(룸 · 칠판 · 전시관 · 리플레이)이 쓴다.
 */
export type PaletteSet = ReadonlyMap<string, Palette>;

/** 팔레트 하나 또는 이름별 묶음 — 렌더러 입력 어디서나 받는다 */
export type PaletteLike = Palette | PaletteSet;

/** 묶음이면 appearance 의 이름으로 고른다(없으면 null). 하나면 그대로(이름 대조는 resolveAppearance 가 한다) */
export function pickPalette(
  palette: PaletteLike | null | undefined,
  name: string | null | undefined,
): Palette | null {
  if (!palette) return null;
  if (palette instanceof Map) return name ? (palette.get(name) ?? null) : null;
  return palette as Palette;
}

export type ResolveReason = "ok" | "no-appearance" | "no-palette" | "palette-mismatch";

export interface ResolvedAppearance {
  /** 인스턴스 id → "#rrggbb"(소문자 sRGB) */
  colors: Map<string, string>;
  /** 팔레트에 없는 키라 무시한 인스턴스 id */
  unknownKeys: string[];
  reason: ResolveReason;
}

const HEX = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

/** "#ABC" · "abc" · "#AABBCC" → "#aabbcc", 형식이 틀리면 null */
export function normalizeHex(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const m = HEX.exec(v.trim());
  if (!m?.[1]) return null;
  const h = m[1].toLowerCase();
  return `#${h.length === 3 ? [...h].map((c) => c + c).join("") : h}`;
}

/**
 * 받은 JSON → Palette. 계약 확정 전이라 두 모양을 받는다:
 *  ① `{palette, colors:[{key, srgb}]}`(mocks 픽스처 · CRQ-002 초안)  ② `{palette, colors:{key: "#hex"}}`
 * 쓸 수 없으면 null(→ 모형 원래 색).
 */
export function normalizePalette(raw: unknown): Palette | null {
  const o = raw as { palette?: unknown; id?: unknown; colors?: unknown } | null;
  if (!o || typeof o !== "object") return null;
  const id = typeof o.palette === "string" ? o.palette : typeof o.id === "string" ? o.id : null;
  if (!id) return null;
  const out: PaletteColor[] = [];
  if (Array.isArray(o.colors)) {
    for (const c of o.colors as { key?: unknown; id?: unknown; srgb?: unknown; hex?: unknown }[]) {
      const key = typeof c?.key === "string" ? c.key : typeof c?.id === "string" ? c.id : null;
      const hex = normalizeHex(c?.srgb ?? c?.hex);
      if (key && hex) out.push({ key, srgb: hex });
    }
  } else if (o.colors && typeof o.colors === "object") {
    for (const [key, v] of Object.entries(o.colors as Record<string, unknown>)) {
      const hex = normalizeHex(v);
      if (hex) out.push({ key, srgb: hex });
    }
  }
  return { palette: id, colors: out };
}

/**
 * appearance + 팔레트 → 인스턴스별 색. 팔레트가 없거나 버전이 다르면 빈 맵(모형 원래 색).
 * 팔레트 묶음({@link PaletteSet})이면 appearance.palette 이름의 것을 쓴다 — 이름 대조 규칙(palette-mismatch)은 그대로.
 */
export function resolveAppearance(
  appearance: Appearance | null | undefined,
  paletteIn: PaletteLike | null | undefined,
): ResolvedAppearance {
  const colors = new Map<string, string>();
  if (!appearance || !appearance.colors || Object.keys(appearance.colors).length === 0)
    return { colors, unknownKeys: [], reason: "no-appearance" };
  const palette = pickPalette(paletteIn, appearance.palette);
  if (!palette) return { colors, unknownKeys: [], reason: "no-palette" };
  if (palette.palette !== appearance.palette)
    return { colors, unknownKeys: [], reason: "palette-mismatch" };
  const byKey = new Map(palette.colors.map((c) => [c.key, normalizeHex(c.srgb)]));
  const unknownKeys: string[] = [];
  for (const [instanceId, key] of Object.entries(appearance.colors)) {
    const hex = byKey.get(key);
    if (hex) colors.set(instanceId, hex);
    else unknownKeys.push(instanceId);
  }
  return { colors, unknownKeys, reason: "ok" };
}

/** 부품 인스턴스 노드 이름 규칙(Campus legomall 조립 GLB): `<부품번호>_<일련번호>` — 예: "6275844_101" */
export const INSTANCE_ID = /^\d+_\d+$/;

export function isInstanceId(name: string | undefined | null): name is string {
  return !!name && INSTANCE_ID.test(name);
}

/** 색 적용 계획 한 줄 — 어떤 메시의 어떤 원본 재질을 어떤 색 사본으로 바꿀지 */
export interface NodeLike {
  /** 원본 GLB 노드 이름(인스턴스 id 판정용) */
  instanceId?: string | null;
  /**
   * 부품 종류 — 조립 GLB 인스턴스 노드(extras 에 `part` 가 있는 노드)의 extras `kind`(hub · motor · sensor · wheel …).
   * 인스턴스 노드가 아니거나 옛 GLB(kind 없음)면 null/생략.
   */
  kind?: string | null;
  children: readonly NodeLike[];
}

/**
 * 칠하지 않는 부품 종류 — 허브 · 모터 · 센서(사용자 결정 2026-09-30 · Campus · Bench · Field 같은 규칙).
 * 신호는 조립 GLB 인스턴스 노드 extras 의 `kind`(모터 회전판 `<id>#hub` 노드도 `kind: "motor"`).
 * 이 종류의 인스턴스는 저장된 색이 있어도 무시하고 원래 재질로 그리며, 위에서 내려온 색도 여기서 끊긴다.
 * `kind` 가 없는 노드(옛 GLB)는 종전대로.
 */
export const UNPAINTABLE_KINDS: ReadonlySet<string> = new Set(["hub", "motor", "sensor"]);

export const isPaintLocked = (kind: string | null | undefined): boolean =>
  !!kind && UNPAINTABLE_KINDS.has(kind);

/**
 * three 노드 userData(GLTFLoader 가 노드 extras 를 옮겨 둔 곳) → 부품 종류. `part` 가 있는 인스턴스 노드의 `kind` 만 본다.
 */
export function partKindOf(userData: Record<string, unknown> | null | undefined): string | null {
  if (!userData || userData.part == null) return null;
  return typeof userData.kind === "string" ? userData.kind : null;
}

/**
 * 트리를 내려가며 각 노드가 받을 색을 정한다. 인스턴스 노드에 색이 있으면 그 아래 전부가 그 색이고,
 * 더 깊은 인스턴스 노드가 자기 색을 가지면 그 색이 이긴다(가장 가까운 조상 우선). 색 없는 노드는 원래 색.
 * 허브 · 모터 · 센서 노드({@link isPaintLocked})는 제 색을 받지 않고 내려온 색도 끊는다 — 그 아래는 원래 색
 * (그 아래 다른 인스턴스 노드가 제 색을 가지면 그 색).
 */
export function planColors<N extends NodeLike>(
  root: N,
  colors: ReadonlyMap<string, string>,
): Map<N, string> {
  const out = new Map<N, string>();
  const walk = (n: N, inherited: string | null) => {
    const locked = isPaintLocked(n.kind);
    const own = n.instanceId && !locked ? (colors.get(n.instanceId) ?? null) : null;
    const c = locked ? null : (own ?? inherited);
    if (c) out.set(n, c);
    for (const ch of n.children) walk(ch as N, c);
  };
  walk(root, null);
  return out;
}

/**
 * FIELD-DEC-005 #4 (B) — 칠할 수 있는 재질인가. 투명 · 금속 재질은 학생 색을 받지 않고 원래 색으로 남는다(허브 창 · 금속 접점).
 * 세 화면(Campus `AssemblyViewer` · Field 여기 · Bench `board3d/build.ts setPaint`)이 같은 규칙 · 같은 시험 표를 쓴다.
 *  - 투명: `transparent === true` 또는 `opacity < 1`
 *  - 금속: `metalness >= 0.5`({@link PAINT_METALNESS_MIN})
 * 값이 없는 재질(Basic · Lambert 등)은 불투명(opacity 1) · 비금속(metalness 0)으로 본다.
 */
export const PAINT_METALNESS_MIN = 0.5;

/** 판정에 쓰는 재질 값만 — three 없이 시험한다 */
export interface MaterialLike {
  transparent?: boolean;
  opacity?: number;
  metalness?: number;
}

export function isPaintable(m: MaterialLike): boolean {
  if (m.transparent === true) return false;
  if (typeof m.opacity === "number" && m.opacity < 1) return false;
  if (typeof m.metalness === "number" && m.metalness >= PAINT_METALNESS_MIN) return false;
  return true;
}

/**
 * 메시의 재질(하나 또는 배열)마다 `paintable`(기본 isPaintable)이면 `paint` 한 것, 아니면 원래 재질. 하나도 안 바뀌면 원래 값 그대로(같은 참조)를 돌려준다.
 */
export function paintEach<M extends MaterialLike>(
  material: M | M[],
  paint: (m: M) => M,
  paintable: (m: M) => boolean = isPaintable,
): M | M[] {
  if (Array.isArray(material)) {
    const out = material.map((m) => (paintable(m) ? paint(m) : m));
    return out.some((m, i) => m !== material[i]) ? out : material;
  }
  return paintable(material) ? paint(material) : material;
}

/** 재질 사본 캐시 키 — 같은 원본 재질 × 같은 색은 사본 하나를 나눠 쓴다 */
export const materialKey = (materialUuid: string, hex: string) => `${materialUuid}|${hex}`;
